import React, { useEffect, useState, useCallback } from 'react';
import { icpLearningAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Target, Brain, TrendingUp, TrendingDown, Loader2, RefreshCw,
  CheckCircle, XCircle, ArrowRight, Lightbulb, AlertTriangle,
  Sparkles, History, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import WhatsWorkingPanel from '../components/dashboard/WhatsWorkingPanel';

export default function ICPLearningPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refining, setRefining] = useState(false);
  const [refinement, setRefinement] = useState(null);
  const [applying, setApplying] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const [planGate, setPlanGate] = useState(null); // { required_plan, upgrade_url } when 403

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await icpLearningAPI.getAnalysis();
      setData(res.data);
      setPlanGate(null);
    } catch (e) {
      if (e?.response?.status === 403) {
        setPlanGate(e.response.data?.detail || { required_plan: 'growth', upgrade_url: '/billing' });
      } else {
        toast.error('Failed to load ICP learning data');
      }
    }
    finally { setLoading(false); }
  }, []);

  const handleRefine = useCallback(async () => {
    setRefining(true);
    try {
      const res = await icpLearningAPI.refineICP();
      setRefinement(res.data);
      toast.success('ICP refinement analysis complete');
    } catch { toast.error('Failed to analyze'); }
    finally { setRefining(false); }
  }, []);

  const handleApply = useCallback(async (learningId) => {
    setApplying(true);
    try {
      await icpLearningAPI.applyRefinement(learningId);
      toast.success('ICP updated to new version');
      setRefinement(null);
      fetchData();
    } catch { toast.error('Failed to apply'); }
    finally { setApplying(false); }
  }, [fetchData]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return (
    <div className="flex items-center justify-center h-96" data-testid="icp-learning-loading">
      <Loader2 className="w-7 h-7 animate-spin text-[hsl(204,94%,40%)]" />
    </div>
  );

  // Plan gate — starter/free tenants see an upgrade card instead of a blank screen
  if (planGate) {
    return (
      <div className="max-w-2xl mx-auto p-6 md:p-10" data-testid="icp-learning-plan-gate">
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-amber-400" />
              </div>
              <CardTitle>Learning loop is a Growth feature</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              ICP Learning analyzes every deal you win or lose, then refines your Ideal Customer Profile
              so the brain scores the next prospect more accurately. Included in the <strong>Growth</strong> plan and above.
            </p>
            <Button onClick={() => { window.location.href = planGate.upgrade_url || '/billing'; }}
              className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="icp-learning-upgrade-btn">
              <Sparkles className="w-4 h-4 mr-2" />Upgrade to unlock
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  const { stats, won_patterns, lost_patterns, current_icp, icp_version, icp_versions, learning_history } = data;

  return (
    <div className="space-y-8 p-4 md:p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="icp-learning-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <div className="p-2 rounded-xl bg-[hsl(171,66%,35%)]/10">
              <Target className="w-5 h-5 text-[hsl(171,66%,35%)]" />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              ICP Learning
            </h1>
            {icp_version > 0 && (
              <Badge variant="secondary" className="bg-[hsl(171,66%,35%)]/10 text-[hsl(171,66%,35%)] border-0 text-xs">
                v{icp_version}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground ml-12">
            Continuously refine your Ideal Customer Profile from deal outcomes
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-icp-btn">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={CheckCircle} label="Won Deals" value={stats.total_won} variant="success" testId="stat-won" />
        <StatCard icon={XCircle} label="Lost Deals" value={stats.total_lost} variant="danger" testId="stat-lost" />
        <StatCard icon={TrendingUp} label="Win Rate" value={`${stats.win_rate}%`} variant="highlight" testId="stat-win-rate" />
        <StatCard icon={Brain} label="Data Status" value={stats.enough_data ? 'Ready' : 'Needs Data'} variant={stats.enough_data ? 'success' : 'muted'} testId="stat-data-status" />
      </div>

      {/* Win/Loss Patterns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <PatternCard title="Won Deal Patterns" patterns={won_patterns} icon={TrendingUp} variant="success" testId="won-patterns" />
        <PatternCard title="Lost Deal Patterns" patterns={lost_patterns} icon={TrendingDown} variant="danger" testId="lost-patterns" />
      </div>

      {/* AI Refinement Section */}
      <Card className="border-0 shadow-sm overflow-hidden" data-testid="refinement-panel">
        <div className="h-0.5 bg-gradient-to-r from-[hsl(171,66%,35%)] to-[hsl(204,94%,40%)]" />
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              <div className="p-1.5 rounded-lg bg-[hsl(204,94%,40%)]/10">
                <Brain className="w-4 h-4 text-[hsl(204,94%,40%)]" />
              </div>
              AI Refinement Engine
            </CardTitle>
            <Button onClick={handleRefine} disabled={refining} size="sm" className="gap-1.5" data-testid="refine-icp-btn">
              {refining ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {refinement ? 'Re-analyze' : 'Analyze Deals'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {refining ? (
            <div className="flex items-center justify-center py-12 gap-3" data-testid="refining-loading">
              <div className="relative">
                <div className="w-10 h-10 border-2 border-[hsl(171,66%,35%)]/20 border-t-[hsl(171,66%,35%)] rounded-full animate-spin" />
                <Brain className="w-4 h-4 text-[hsl(171,66%,35%)] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
              </div>
              <div>
                <p className="text-sm font-medium">Analyzing your deal outcomes...</p>
                <p className="text-[11px] text-muted-foreground">Finding patterns to improve your ICP</p>
              </div>
            </div>
          ) : refinement ? (
            <div className="space-y-5">
              {/* Overall recommendation */}
              {refinement.overall_recommendation && (
                <div className="p-4 rounded-xl bg-sky-50 border border-sky-200/60">
                  <p className="text-sm font-medium text-sky-800">{refinement.overall_recommendation}</p>
                </div>
              )}

              {/* Refinements */}
              {refinement.refinements?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2"><ArrowRight className="w-4 h-4" /> Proposed Refinements</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {refinement.refinements.map((r, i) => (
                      <div key={i} className="p-3.5 rounded-xl border border-border bg-card hover:shadow-sm transition-all" data-testid={`refinement-${i}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="secondary" className="bg-[hsl(171,66%,35%)]/10 text-[hsl(171,66%,35%)] border-0 text-[10px]">
                            {r.area}
                          </Badge>
                          <span className={`w-1.5 h-1.5 rounded-full ${r.confidence === 'high' ? 'bg-emerald-400' : r.confidence === 'medium' ? 'bg-amber-400' : 'bg-slate-300'}`} />
                        </div>
                        <p className="text-xs text-muted-foreground mb-1">
                          <span className="line-through">{r.current}</span> <ArrowRight className="w-3 h-3 inline mx-1" /> <span className="font-medium text-foreground">{r.recommended}</span>
                        </p>
                        <p className="text-xs text-muted-foreground">{r.reason}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Win/Loss factors */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {refinement.win_factors?.length > 0 && (
                  <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200/60">
                    <h5 className="text-xs font-medium text-emerald-700 mb-2 flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5" /> Win Factors</h5>
                    {refinement.win_factors.map((f, i) => (
                      <p key={i} className="text-xs text-emerald-700/80 mb-1">- {f}</p>
                    ))}
                  </div>
                )}
                {refinement.loss_factors?.length > 0 && (
                  <div className="p-3 rounded-lg bg-rose-50 border border-rose-200/60">
                    <h5 className="text-xs font-medium text-rose-700 mb-2 flex items-center gap-1.5"><XCircle className="w-3.5 h-3.5" /> Loss Factors</h5>
                    {refinement.loss_factors.map((f, i) => (
                      <p key={i} className="text-xs text-rose-700/80 mb-1">- {f}</p>
                    ))}
                  </div>
                )}
              </div>

              {/* Apply button */}
              <div className="flex justify-end pt-2">
                <Button onClick={() => handleApply(refinement.learning_id)} disabled={applying} className="gap-2 bg-[hsl(171,66%,35%)] hover:bg-[hsl(171,66%,40%)]" data-testid="apply-refinement-btn">
                  {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Apply & Evolve ICP to v{(icp_version || 0) + 1}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="refinement-empty">
              <div className="p-4 rounded-2xl bg-gradient-to-br from-[hsl(171,66%,35%)]/5 to-[hsl(204,94%,40%)]/5 mb-4">
                <Brain className="w-10 h-10 text-muted-foreground/30" />
              </div>
              <p className="text-sm font-medium mb-1">Analyze your deal outcomes</p>
              <p className="text-xs text-muted-foreground max-w-sm">The Brain will study your won and lost deals to find patterns and suggest improvements to your Ideal Customer Profile.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ICP Version History */}
      {learning_history?.length > 0 && (
        <div>
          <button onClick={() => setShowHistory(!showHistory)} className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors mb-3" data-testid="toggle-history">
            {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            <History className="w-4 h-4" /> Learning History ({learning_history.length})
          </button>
          {showHistory && (
            <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
              {learning_history.map((h, i) => (
                <div key={h.id} className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/30 transition-colors" data-testid={`history-${i}`}>
                  <div>
                    <p className="text-sm font-medium">{h.overall_recommendation?.slice(0, 80) || 'Refinement analysis'}...</p>
                    <p className="text-[11px] text-muted-foreground">
                      {h.won_count} won, {h.lost_count} lost - {new Date(h.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge variant={h.applied ? 'default' : 'secondary'} className={h.applied ? 'bg-emerald-100 text-emerald-700 border-0' : ''}>
                    {h.applied ? `Applied (v${h.new_version})` : 'Pending'}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* What's Working — outcome-based messaging learning */}
      <WhatsWorkingPanel />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, variant, testId }) {
  const colors = {
    success: 'text-emerald-600 bg-emerald-50',
    danger: 'text-rose-600 bg-rose-50',
    highlight: 'text-[hsl(171,66%,35%)] bg-[hsl(171,66%,35%)]/10',
    muted: 'text-muted-foreground bg-muted',
  };
  const c = colors[variant] || colors.muted;
  return (
    <Card className="border-0 shadow-sm" data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className={`p-1.5 rounded-lg ${c.split(' ')[1]}`}><Icon className={`w-3.5 h-3.5 ${c.split(' ')[0]}`} /></div>
          <span className="text-[11px] text-muted-foreground font-medium uppercase tracking-widest">{label}</span>
        </div>
        <p className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{value}</p>
      </CardContent>
    </Card>
  );
}

function PatternCard({ title, patterns, icon: Icon, variant, testId }) {
  const isSuccess = variant === 'success';
  if (!patterns || patterns.count === 0) return (
    <Card className="border-0 shadow-sm" data-testid={testId}>
      <CardContent className="flex flex-col items-center justify-center h-40 text-muted-foreground/50">
        <Icon className="w-6 h-6 mb-1" /><p className="text-xs">No {isSuccess ? 'won' : 'lost'} deals yet</p>
      </CardContent>
    </Card>
  );
  return (
    <Card className={`border-0 shadow-sm ${isSuccess ? 'ring-1 ring-emerald-200/50' : 'ring-1 ring-rose-200/50'}`} data-testid={testId}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
          <Icon className={`w-4 h-4 ${isSuccess ? 'text-emerald-500' : 'text-rose-500'}`} /> {title}
          <Badge variant="secondary" className="border-0 text-[10px]">{patterns.count}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {Object.entries(patterns.companies || {}).length > 0 && (
          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1">Companies</p>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(patterns.companies).slice(0, 5).map(([k, v]) => (
                <Badge key={k} variant="secondary" className="text-[10px] border-0">{k} ({v})</Badge>
              ))}
            </div>
          </div>
        )}
        {Object.entries(patterns.titles || {}).length > 0 && (
          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1">Titles</p>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(patterns.titles).slice(0, 5).map(([k, v]) => (
                <Badge key={k} variant="secondary" className="text-[10px] border-0">{k} ({v})</Badge>
              ))}
            </div>
          </div>
        )}
        <div className="flex gap-4">
          {patterns.avg_value > 0 && <p className="text-xs text-muted-foreground">Avg Value: <span className="font-medium text-foreground">${patterns.avg_value.toLocaleString()}</span></p>}
          {patterns.avg_score > 0 && <p className="text-xs text-muted-foreground">Avg Score: <span className="font-medium text-foreground">{patterns.avg_score}</span></p>}
        </div>
      </CardContent>
    </Card>
  );
}
